import { 
  SatelliteRecord, 
  CloseApproachEvent, 
  CloseApproachConfig, 
  ECIPosition 
} from '../types/satellite';
import { OrbitalMechanics } from './orbitalMechanics';

/**
 * Spatial grid for efficient collision detection
 */
class SpatialGrid {
  private cellSize: number;
  private grid = new Map<string, number[]>();
  
  constructor(cellSizeKm: number) {
    this.cellSize = cellSizeKm;
  }
  
  /**
   * Generate spatial hash key for position
   */
  private getKey(position: ECIPosition): string {
    const ix = Math.floor(position.x / this.cellSize);
    const iy = Math.floor(position.y / this.cellSize);
    const iz = Math.floor(position.z / this.cellSize);
    return `${ix}|${iy}|${iz}`;
  }
  
  /**
   * Add satellite to spatial grid
   */
  addSatellite(satelliteIndex: number, position: ECIPosition): void {
    const key = this.getKey(position);
    const cell = this.grid.get(key) ?? [];
    cell.push(satelliteIndex);
    this.grid.set(key, cell);
  }
  
  /**
   * Get neighboring grid cells for collision detection
   */
  private getNeighborKeys(key: string): string[] {
    const [ix, iy, iz] = key.split('|').map(Number);
    const neighbors: string[] = [];
    
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          neighbors.push(`${ix + dx}|${iy + dy}|${iz + dz}`);
        }
      }
    }
    
    return neighbors;
  }
  
  /**
   * Get potential collision pairs from spatial grid
   */
  getPotentialCollisions(): Array<[number, number]> {
    const pairs: Array<[number, number]> = [];
    const processedCells = new Set<string>();
    
    for (const [key, satellites] of this.grid.entries()) {
      if (satellites.length < 2 || processedCells.has(key)) {
        continue;
      }
      
      // Collect candidates from this cell and its neighbors
      const candidates = new Set(satellites);
      const neighborKeys = this.getNeighborKeys(key);
      
      for (const neighborKey of neighborKeys) {
        const neighbors = this.grid.get(neighborKey);
        if (neighbors) {
          neighbors.forEach(sat => candidates.add(sat));
        }
      }
      
      // Generate pairs from candidates
      const candidateArray = Array.from(candidates);
      for (let i = 0; i < candidateArray.length; i++) {
        for (let j = i + 1; j < candidateArray.length; j++) {
          pairs.push([candidateArray[i], candidateArray[j]]);
        }
      }
      
      processedCells.add(key);
    }
    
    return pairs;
  }
  
  /**
   * Clear the grid for next time step
   */
  clear(): void {
    this.grid.clear();
  }
}

export class CollisionDetectionService {
  
  /**
   * Detect close approach events using spatial hashing
   */
  static async detectCloseApproaches(
    satellites: SatelliteRecord[],
    config: CloseApproachConfig
  ): Promise<CloseApproachEvent[]> {
    
    const events: CloseApproachEvent[] = [];
    const now = new Date();
    const steps = Math.max(1, Math.floor(config.horizonSec / config.stepSec));
    
    for (let step = 0; step <= steps; step++) {
      const time = new Date(now.getTime() + step * config.stepSec * 1000);
      const stepEvents = await this.analyzeTimeStep(satellites, time, config);
      events.push(...stepEvents);
      
      // Stop if we've reached the maximum number of events
      if (events.length >= config.maxPairs) {
        break;
      }
    }
    
    return events.slice(0, config.maxPairs);
  }
  
  /**
   * Analyze a single time step for close approaches
   */
  private static async analyzeTimeStep(
    satellites: SatelliteRecord[],
    time: Date,
    config: CloseApproachConfig
  ): Promise<CloseApproachEvent[]> {
    
    const events: CloseApproachEvent[] = [];
    const grid = new SpatialGrid(config.cellKm);
    
    // Cache ECI positions for this time step
    const eciPositions: (ECIPosition | null)[] = [];
    
    // Populate spatial grid
    for (let i = 0; i < satellites.length; i++) {
      const position = OrbitalMechanics.getECIPosition(satellites[i].satrec, time);
      eciPositions[i] = position;
      
      if (position) {
        grid.addSatellite(i, position);
      }
    }
    
    // Get potential collision pairs from spatial grid
    const pairs = grid.getPotentialCollisions();
    
    // Check actual distances for potential pairs
    for (const [i, j] of pairs) {
      const pos1 = eciPositions[i];
      const pos2 = eciPositions[j];
      
      if (!pos1 || !pos2) continue;
      
      const distance = OrbitalMechanics.calculateDistance(pos1, pos2);
      
      if (distance <= config.thresholdKm) {
        events.push({
          t: time.toISOString(),
          i,
          j,
          dKm: Number(distance.toFixed(3)),
          a: satellites[i].name,
          b: satellites[j].name,
        });
      }
    }
    
    return events;
  }
  
  /**
   * Calculate collision probability (simplified model)
   * In a real system, this would use covariance matrices and Monte Carlo methods
   */
  static calculateCollisionProbability(
    sat1: SatelliteRecord,
    sat2: SatelliteRecord,
    time: Date,
    uncertaintyKm: number = 1.0
  ): number {
    const pos1 = OrbitalMechanics.getECIPosition(sat1.satrec, time);
    const pos2 = OrbitalMechanics.getECIPosition(sat2.satrec, time);
    
    if (!pos1 || !pos2) return 0;
    
    const distance = OrbitalMechanics.calculateDistance(pos1, pos2);
    
    // Simplified probability model using Gaussian distribution
    // In reality, this requires proper covariance propagation
    const sigma = uncertaintyKm;
    const pc = Math.exp(-0.5 * Math.pow(distance / sigma, 2));
    
    return Math.min(pc, 1.0);
  }
  
  /**
   * Sort close approach events by criticality
   */
  static sortEventsByCriticality(events: CloseApproachEvent[]): CloseApproachEvent[] {
    return [...events].sort((a, b) => {
      // Sort by distance (closer = higher priority)
      if (a.dKm !== b.dKm) {
        return a.dKm - b.dKm;
      }
      
      // Then by time (sooner = higher priority)
      return new Date(a.t).getTime() - new Date(b.t).getTime();
    });
  }
  
  /**
   * Filter events by minimum time separation to reduce noise
   */
  static filterByTimeSeparation(
    events: CloseApproachEvent[],
    minSeparationSeconds: number = 300
  ): CloseApproachEvent[] {
    const filtered: CloseApproachEvent[] = [];
    const lastEventTime = new Map<string, number>();
    
    for (const event of events) {
      const pairKey = `${Math.min(event.i, event.j)}-${Math.max(event.i, event.j)}`;
      const eventTime = new Date(event.t).getTime();
      const lastTime = lastEventTime.get(pairKey) ?? 0;
      
      if (eventTime - lastTime >= minSeparationSeconds * 1000) {
        filtered.push(event);
        lastEventTime.set(pairKey, eventTime);
      }
    }
    
    return filtered;
  }
}